import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { analyticsAPI } from '../../services/api';
import { toast } from 'react-hot-toast';
import DashboardLayout from '../../components/layout/DashboardLayout';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import DateRangePicker from '../../components/common/DateRangePicker';

interface ReportData {
  dateRange: {
    start: string;
    end: string;
  };
  summary: {
    totalVisitors: number;
    totalPageViews: number;
    totalEvents: number;
    bounceRate: number;
    avgSessionDuration: number;
  };
  topPages: Array<{ url: string; title: string; views: number }>;
  topEvents: Array<{ eventName: string; category: string; count: number; totalValue: number | null }>;
  dailyTrend: Array<{ date: string; visitors: number; pageViews: number }>;
  countries: Array<{ country: string; count: number }>;
  devices: Array<{ device: string; count: number }>;
  browsers: Array<{ browser: string; count: number }>;
  trafficSources: Array<{ source: string; count: number }>;
}

const ReportPage: React.FC = () => {
  useDocumentTitle('Reports');
  const { currentAsset } = useAuth();
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [startDateObj, setStartDateObj] = useState<Date | null>(() => {
    const d = new Date(); d.setDate(d.getDate() - 30); return d;
  });
  const [endDateObj, setEndDateObj] = useState<Date | null>(() => new Date());

  const startDate = startDateObj ? startDateObj.toISOString().split('T')[0] : '';
  const endDate = endDateObj ? endDateObj.toISOString().split('T')[0] : '';

  useEffect(() => {
    if (currentAsset) {
      fetchReportData();
    }
  }, [currentAsset]);

  const fetchReportData = async () => {
    if (!currentAsset) return;

    setLoading(true);
    try {
      const response = await analyticsAPI.getReportData(currentAsset.id, startDate, endDate);
      setReportData(response.data.data);
    } catch (error: any) {
      console.error('Failed to fetch report data:', error);
      toast.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  const handlePresetClick = (days: number) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    setStartDateObj(start);
    setEndDateObj(end);
  };

  const handleThisMonth = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    setStartDateObj(start);
    setEndDateObj(now);
  };

  const handleLastMonth = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const end = new Date(now.getFullYear(), now.getMonth(), 0);
    setStartDateObj(start);
    setEndDateObj(end);
  };

  const handleGenerateReport = () => {
    fetchReportData();
  };

  const downloadPdf = async () => {
    if (!currentAsset) return;

    setDownloading(true);
    try {
      toast.loading('Generating PDF report...');
      const response = await analyticsAPI.getReportPdf(currentAsset.id, startDate, endDate);

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `analytics-report-${currentAsset.name}-${startDate}-to-${endDate}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.dismiss();
      toast.success('Report downloaded successfully!');
    } catch (error: any) {
      toast.dismiss();
      console.error('Failed to download PDF:', error);
      toast.error('Failed to download report');
    } finally {
      setDownloading(false);
    }
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat().format(num);
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  if (!currentAsset && !loading) {
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <div className="text-center">
            <i className="bi bi-box-seam fs-1 text-muted d-block mb-3"></i>
            <h4>No Asset Selected</h4>
            <p className="text-muted mb-4">Please add an asset to view reports</p>
            <a href="/setup/asset" className="btn btn-primary">
              <i className="bi bi-plus-circle me-2"></i>Add Your First Asset
            </a>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="container-fluid">
        {/* Page Header */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
              <div>
                <h2 className="h3 mb-1">Analytics Report</h2>
                <p className="text-muted mb-0">Generate and download comprehensive analytics reports</p>
              </div>
              <button
                className="btn btn-primary"
                onClick={downloadPdf}
                disabled={downloading || !reportData}
              >
                {downloading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Generating...
                  </>
                ) : (
                  <>
                    <i className="bi bi-file-earmark-pdf me-2"></i>
                    Download PDF
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Date Range Selection */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <div className="d-flex flex-wrap gap-3 align-items-center">
                  <div>
                    <div className="small text-muted mb-1">Date Range</div>
                    <DateRangePicker
                      startDate={startDateObj}
                      endDate={endDateObj}
                      onChange={(s, e) => { setStartDateObj(s); setEndDateObj(e); }}
                      showActions={false}
                      maxDate={new Date()}
                    />
                  </div>
                  <div>
                    <div className="small text-muted mb-1">Quick Presets</div>
                    <div className="btn-group">
                      <button className="btn btn-outline-secondary btn-sm" onClick={() => handlePresetClick(7)}>7 Days</button>
                      <button className="btn btn-outline-secondary btn-sm" onClick={() => handlePresetClick(30)}>30 Days</button>
                      <button className="btn btn-outline-secondary btn-sm" onClick={() => handlePresetClick(90)}>90 Days</button>
                      <button className="btn btn-outline-secondary btn-sm" onClick={handleThisMonth}>This Month</button>
                      <button className="btn btn-outline-secondary btn-sm" onClick={handleLastMonth}>Last Month</button>
                    </div>
                  </div>
                  <div className="ms-auto">
                    <div className="small text-muted mb-1">&nbsp;</div>
                    <button
                      className="btn btn-primary"
                      onClick={handleGenerateReport}
                      disabled={loading}
                    >
                      {loading ? (
                        <span className="spinner-border spinner-border-sm" role="status"></span>
                      ) : (
                        <><i className="bi bi-arrow-repeat me-2"></i>Generate</>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
            <div className="text-center">
              <div className="spinner-border text-primary mb-3" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="text-muted">Loading report data...</p>
            </div>
          </div>
        ) : reportData ? (
          <>
            {/* Summary Stats */}
            <div className="row g-4 mb-4">
              <div className="col-12 col-md-6 col-xl-2">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body text-center">
                    <i className="bi bi-people fs-2 text-primary mb-2"></i>
                    <h3 className="h4 mb-1">{formatNumber(reportData.summary.totalVisitors)}</h3>
                    <p className="text-muted small mb-0">Total Visitors</p>
                  </div>
                </div>
              </div>
              <div className="col-12 col-md-6 col-xl-2">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body text-center">
                    <i className="bi bi-eye fs-2 text-success mb-2"></i>
                    <h3 className="h4 mb-1">{formatNumber(reportData.summary.totalPageViews)}</h3>
                    <p className="text-muted small mb-0">Page Views</p>
                  </div>
                </div>
              </div>
              <div className="col-12 col-md-6 col-xl-2">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body text-center">
                    <i className="bi bi-lightning fs-2 text-warning mb-2"></i>
                    <h3 className="h4 mb-1">{formatNumber(reportData.summary.totalEvents)}</h3>
                    <p className="text-muted small mb-0">Events</p>
                  </div>
                </div>
              </div>
              <div className="col-12 col-md-6 col-xl-3">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body text-center">
                    <i className="bi bi-arrow-return-left fs-2 text-danger mb-2"></i>
                    <h3 className="h4 mb-1">{reportData.summary.bounceRate}%</h3>
                    <p className="text-muted small mb-0">Bounce Rate</p>
                  </div>
                </div>
              </div>
              <div className="col-12 col-md-6 col-xl-3">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body text-center">
                    <i className="bi bi-clock fs-2 text-info mb-2"></i>
                    <h3 className="h4 mb-1">{formatDuration(reportData.summary.avgSessionDuration)}</h3>
                    <p className="text-muted small mb-0">Avg. Session</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Top Pages & Events */}
            <div className="row g-4 mb-4">
              <div className="col-12 col-lg-6">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-white">
                    <h5 className="mb-0">Top Pages</h5>
                  </div>
                  <div className="card-body p-0">
                    {reportData.topPages.length > 0 ? (
                      <div className="table-responsive">
                        <table className="table table-hover mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>Page</th>
                              <th className="text-end">Views</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reportData.topPages.slice(0, 10).map((page, index) => (
                              <tr key={index}>
                                <td>
                                  <div className="text-truncate" style={{ maxWidth: '250px' }}>
                                    {page.title || page.url}
                                  </div>
                                </td>
                                <td className="text-end">
                                  <span className="badge bg-primary">{formatNumber(page.views)}</span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center text-muted py-4">No page data available</div>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-12 col-lg-6">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-white">
                    <h5 className="mb-0">Top Events</h5>
                  </div>
                  <div className="card-body p-0">
                    {reportData.topEvents.length > 0 ? (
                      <div className="table-responsive">
                        <table className="table table-hover mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>Event</th>
                              <th>Category</th>
                              <th className="text-end">Count</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reportData.topEvents.slice(0, 10).map((event, index) => (
                              <tr key={index}>
                                <td>{event.eventName}</td>
                                <td>
                                  <span className="badge bg-secondary">{event.category}</span>
                                </td>
                                <td className="text-end">{formatNumber(event.count)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center text-muted py-4">No event data available</div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Daily Trend */}
            <div className="row g-4 mb-4">
              <div className="col-12">
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-white">
                    <h5 className="mb-0">Daily Traffic Trend</h5>
                  </div>
                  <div className="card-body">
                    {reportData.dailyTrend.length > 0 ? (
                      <div className="table-responsive">
                        <table className="table table-sm">
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th className="text-end">Visitors</th>
                              <th className="text-end">Page Views</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reportData.dailyTrend.map((day, index) => (
                              <tr key={index}>
                                <td>{new Date(day.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                                <td className="text-end">{formatNumber(day.visitors)}</td>
                                <td className="text-end">{formatNumber(day.pageViews)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center text-muted py-4">No trend data available</div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Demographics */}
            <div className="row g-4">
              <div className="col-12 col-md-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-white">
                    <h5 className="mb-0">Devices</h5>
                  </div>
                  <div className="card-body">
                    {reportData.devices.length > 0 ? (
                      <ul className="list-unstyled mb-0">
                        {reportData.devices.map((device, index) => (
                          <li key={index} className="d-flex justify-content-between py-2 border-bottom">
                            <span>{device.device}</span>
                            <span className="badge bg-light text-dark">{formatNumber(device.count)}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="text-center text-muted">No device data</div>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-12 col-md-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-white">
                    <h5 className="mb-0">Browsers</h5>
                  </div>
                  <div className="card-body">
                    {reportData.browsers.length > 0 ? (
                      <ul className="list-unstyled mb-0">
                        {reportData.browsers.slice(0, 6).map((browser, index) => (
                          <li key={index} className="d-flex justify-content-between py-2 border-bottom">
                            <span>{browser.browser}</span>
                            <span className="badge bg-light text-dark">{formatNumber(browser.count)}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="text-center text-muted">No browser data</div>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-12 col-md-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-white">
                    <h5 className="mb-0">Traffic Sources</h5>
                  </div>
                  <div className="card-body">
                    {reportData.trafficSources.length > 0 ? (
                      <ul className="list-unstyled mb-0">
                        {reportData.trafficSources.map((source, index) => (
                          <li key={index} className="d-flex justify-content-between py-2 border-bottom">
                            <span>{source.source}</span>
                            <span className="badge bg-light text-dark">{formatNumber(source.count)}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="text-center text-muted">No traffic data</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
            <div className="text-center">
              <i className="bi bi-file-earmark-bar-graph fs-1 text-muted d-block mb-3"></i>
              <h4>Generate a Report</h4>
              <p className="text-muted">Select a date range and click Generate to view the report</p>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ReportPage;
